import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function AssistantWidget() {
  const { locale } = useI18n();
  const fr = locale === "fr";
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [starters, setStarters] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const end = useRef(null);

  useEffect(() => {
    if (!open || starters.length) return;
    api.get(`/assistant/starters?locale=${locale}`)
      .then(({ data }) => setStarters(data.starters)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: q, session_id: sid.current, locale,
        page: window.location.pathname,
      });
      sid.current = data.session_id;
      setMsgs((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch {
      setMsgs((m) => [...m, {
        role: "assistant",
        content: fr ? "Problème de connexion. Réessayez ou écrivez à studiomoviesia@gmail.com."
                    : "Connection issue. Try again or write to studiomoviesia@gmail.com.",
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-open"
          aria-label={fr ? "Assistant Studio" : "Studio Assistant"}
          className="btn-amber fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full shadow-[0_10px_40px_rgba(255,75,43,0.45)] transition-transform duration-300 hover:scale-105"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="glass fixed bottom-5 right-5 z-[60] flex h-[min(620px,calc(100vh-40px))] w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-[#2a2c3c] bg-[#0b0b11]/95"
        >
          <div className="flex items-center justify-between border-b border-[#1b1d27] px-5 py-4">
            <div className="flex items-center gap-2.5">
              <Sparkles className="h-4 w-4 text-[#ff4b2b]" />
              <div>
                <p className="font-display text-[13.5px] font-medium">
                  {fr ? "Assistant Studio" : "Studio Assistant"}
                </p>
                <p className="mono text-[9.5px] uppercase tracking-[0.14em] text-[#8288a0]">
                  {fr ? "il connaît tout le studio" : "knows the whole studio"}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" aria-label={fr ? "Fermer l'assistant" : "Close the assistant"} className="btn-ghost rounded-full p-2">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {msgs.length === 0 && (
              <div data-testid="assistant-starters">
                <p className="text-[12.5px] leading-relaxed text-[#9194a6]">
                  {fr ? "Posez n'importe quelle question sur le studio, les moteurs, les crédits ou votre projet. Je réponds en français."
                      : "Ask anything about the studio, the engines, credits or your project."}
                </p>
                <div className="mt-4 space-y-2">
                  {starters.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      className="block w-full rounded-lg border border-[#212230] bg-[#101118] px-3.5 py-2.5 text-left text-[12px] text-[#c0c3d0] transition-colors duration-200 hover:border-[#ff4b2b]/40"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div
                key={i}
                data-testid={`assistant-msg-${m.role}`}
                className={`max-w-[88%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-[12.5px] leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#ff4b2b]/15 text-[#ffd7cb]"
                    : "bg-[#101118] text-[#c0c3d0]"
                }`}
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <p className="mono text-[10px] uppercase tracking-[0.18em] text-[#8288a0]">
                {fr ? "réflexion…" : "thinking…"}
              </p>
            )}
            <div ref={end} />
          </div>

          <div className="flex gap-2 border-t border-[#1b1d27] p-3.5">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              data-testid="assistant-input"
              placeholder={fr ? "Votre question…" : "Your question…"}
              className="flex-1 rounded-lg border border-[#212230] bg-[#101118] px-3.5 py-2.5 text-[12.5px] outline-none focus:border-[#ff4b2b]/60"
            />
            <button
              onClick={() => ask()}
              disabled={busy || !input.trim()}
              data-testid="assistant-send"
              aria-label={fr ? "Envoyer la question" : "Send question"}
              className="btn-amber rounded-lg px-3.5 disabled:opacity-40"
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
